import type { VerticalConfig, VerticalEmailConfig } from '../types/vertical';
import { buildCompleteProfileToken } from './complete-profile-token';
import { getVerticalConfig } from './vertical-registry';

import { Buffer } from 'node:buffer';
import { createHmac, timingSafeEqual } from 'node:crypto';

/**
 * Resolved email sending context for a single vertical.
 *
 * Every email-sending surface (ResendEmailAdapter, hot-reply,
 * ban-fallback) resolves this once per send and uses it for the From:
 * header, the in-body links, and the opt-out URL factory. Centralising
 * this means a DriveZA→Rovvo-style rebrand is a one-line vertical-config
 * change rather than a 30-file search-and-replace.
 *
 * See `docs/architecture/adr/0002-email-vars-per-vertical.md` for the
 * ownership decision (new). The helper itself lives in leadgen because
 * verticals are leadgen-scoped; ChatSuite does not send leadgen emails.
 */
export interface ResolvedEmailContext {
  fromAddress: string;
  fromName: string;
  replyToAddress: string | undefined;
  brandHomepageUrl: string;
  /** `applicationUrlTemplate` with `{city}` substituted. */
  applicationUrl: string;
  privacyPolicyUrl: string;
  unsubscribeMailbox: string | undefined;
  /**
   * Factory that builds a signed opt-out URL for the given lead. Uses
   * the vertical's `brandHomepageUrl` as the host so the click resolves
   * on the domain the recipient sees in the From: header.
   */
  optOutUrl: (leadId: string, channel: 'email' | 'whatsapp') => string;
  /**
   * Factory that builds the signed "complete your profile" form URL for a lead.
   * The field-collection re-processor emails this to leads whose delivery was
   * soft-blocked (`under_qualified`) so they can supply the missing
   * qualification-gate answers. Same host as the From: header.
   */
  completeProfileUrl: (leadId: string) => string;
}

export class EmailContextError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmailContextError';
  }
}

/**
 * Normalise a vertical slug to the SCREAMING_SNAKE_CASE fragment used
 * in env var names. E.g. `spitex-supply` → `SPITEX_SUPPLY`,
 * `chatsuite-ch` → `CHATSUITE_CH`.
 */
function slugToEnvFragment(slug: string): string {
  return slug.toUpperCase().replace(/-/g, '_');
}

/**
 * Substitute `{city}` in the template. Leaves the template unchanged
 * when no token is present. Throws `EmailContextError` when the
 * template has a `{city}` token but no city was supplied and the
 * vertical config has no default city to fall back on.
 */
function resolveApplicationUrl(
  template: string,
  city: string | undefined,
): string {
  if (!template.includes('{city}')) return template;
  if (!city) {
    throw new EmailContextError(
      `applicationUrlTemplate contains {city} but no city was supplied and ` +
        `the vertical has no landingPage.cities fallback`,
    );
  }
  return template.replace('{city}', city);
}

/**
 * Pick the default city for a template substitution. Returns the
 * vertical's `email.defaultCity` when declared; otherwise undefined.
 */
function defaultCityFor(config: VerticalConfig): string | undefined {
  return config.email?.defaultCity;
}

/**
 * Normalise a human city label ("Cape Town") to a URL slug ("cape-town").
 *
 * Lead cities arrive title-cased with spaces from the Meta Instant Form
 * dropdown, while `applicationUrlTemplate` does a literal `{city}`
 * replace, so an unnormalised value would produce
 * `/drivers/Cape Town/apply`.
 */
function citySlug(city: string): string {
  return city
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Choose the city slug to substitute into the application URL.
 *
 * Uses the lead's own city only when it maps to a page that exists,
 * otherwise falls back to the vertical default. Before this existed,
 * `city` was never supplied at all and EVERY drivers email linked to
 * `/drivers/johannesburg/apply` — including the 1,001 leads whose known
 * city was Pretoria, Cape Town or Durban.
 */
function resolveCityForUrl(
  config: VerticalConfig,
  requested: string | undefined,
): string | undefined {
  const fallback = defaultCityFor(config);

  if (!requested) return fallback;

  const supported = config.email?.supportedCities;

  // No allowlist declared: trust the caller. Verticals that pass a city
  // explicitly (rather than deriving it from lead data) are responsible
  // for it being routable, which is how this behaved before the
  // allowlist existed.
  if (!supported || supported.length === 0) return requested;

  const slug = citySlug(requested);

  return supported.includes(slug) ? slug : fallback;
}

/**
 * Resolve the secret used to sign opt-out tokens.
 *
 * The fallback chain is load-bearing and must NOT be reordered: tokens
 * already delivered in production emails were signed with whatever this
 * resolved to at send time, and those links are permanently valid (the
 * token carries no expiry). Every email sent since 2026-03-25 was signed
 * with `CRON_SECRET`, because `OPT_OUT_HMAC_SECRET` has never been set.
 *
 * Setting `OPT_OUT_HMAC_SECRET` later would silently invalidate every
 * unsubscribe link already in someone's inbox. If it ever needs to be
 * introduced, `verifyOptOutToken` must accept BOTH secrets during the
 * transition.
 */
function resolveOptOutSecret(): string {
  return process.env.OPT_OUT_HMAC_SECRET ?? process.env.CRON_SECRET ?? '';
}

/**
 * Compute the opt-out token for a lead + channel pair.
 *
 * Deliberately stable per (leadId, channel): the same lead receives the
 * same token in every email, forever. That is what makes already-delivered
 * links keep working.
 */
export function buildOptOutToken(leadId: string, channel: string): string {
  return createHmac('sha256', resolveOptOutSecret())
    .update(`${leadId}:${channel}`)
    .digest('hex');
}

/**
 * Verify an opt-out token in constant time.
 *
 * Returns false when the signing secret is unset: an empty key would make
 * every token forgeable, so we refuse to verify rather than accept
 * attacker-supplied input. Callers must treat false as "do not opt out".
 */
export function verifyOptOutToken(
  leadId: string,
  channel: string,
  token: string,
): boolean {
  if (resolveOptOutSecret().length === 0) return false;

  const expected = buildOptOutToken(leadId, channel);

  // timingSafeEqual throws on length mismatch, so guard first.
  if (expected.length !== token.length) return false;

  return timingSafeEqual(Buffer.from(expected), Buffer.from(token));
}

export interface GetEmailContextOpts {
  /**
   * City slug to substitute into `applicationUrlTemplate`. When
   * omitted, the helper uses the first city in the vertical's
   * landingPage.cities array.
   */
  city?: string;
}

/**
 * Resolve the full email context for a vertical.
 *
 * Precedence for every field:
 *   1. Env-var override (`EMAIL_FROM_<SLUG>`, `EMAIL_FROM_NAME_<SLUG>`, etc.)
 *   2. `config.email.<field>` from the registered vertical config
 *   3. Sensible per-field default (e.g. `branding.brandName` for fromName,
 *      `NEXT_PUBLIC_SITE_URL` for brandHomepageUrl)
 *
 * Throws `EmailContextError` when the vertical is unknown OR when the
 * vertical registers no `email` block and the env overrides don't cover
 * the required fields. Callers are responsible for handling the error
 * (e.g. hot-reply returns `'failed'`).
 */
export function getEmailContext(
  verticalSlug: string,
  opts?: GetEmailContextOpts,
): ResolvedEmailContext {
  const config = getVerticalConfig(verticalSlug);
  if (!config) {
    throw new EmailContextError(`Unknown vertical: ${verticalSlug}`);
  }

  const emailConfig: VerticalEmailConfig | undefined = config.email;
  const envFragment = slugToEnvFragment(verticalSlug);

  // fromAddress: env > config > RESEND_FROM_ADDRESS > last-resort
  const fromAddress =
    process.env[`EMAIL_FROM_${envFragment}`] ??
    emailConfig?.fromAddress ??
    process.env.RESEND_FROM_ADDRESS ??
    'noreply@notify.akson.ch';

  // fromName: env > config > branding.brandName > 'Leadgen'
  const fromName =
    process.env[`EMAIL_FROM_NAME_${envFragment}`] ??
    emailConfig?.fromName ??
    config.branding?.brandName ??
    'Leadgen';

  // brandHomepageUrl: config > NEXT_PUBLIC_SITE_URL > default
  const brandHomepageUrl =
    emailConfig?.brandHomepageUrl ??
    process.env.NEXT_PUBLIC_SITE_URL ??
    'https://selenos.ai';

  const city = resolveCityForUrl(config, opts?.city);

  let applicationUrl: string;
  if (emailConfig?.applicationUrlTemplate) {
    applicationUrl = resolveApplicationUrl(
      emailConfig.applicationUrlTemplate,
      city,
    );
  } else {
    throw new EmailContextError(
      `Vertical "${verticalSlug}" is missing email.applicationUrlTemplate. ` +
        `Every vertical that sends email must declare this field so ` +
        `recipients have a clear next-step URL.`,
    );
  }

  const privacyPolicyUrl =
    emailConfig?.privacyPolicyUrl ?? `${brandHomepageUrl}/privacy`;

  return {
    fromAddress,
    fromName,
    replyToAddress: emailConfig?.replyToAddress,
    brandHomepageUrl,
    applicationUrl,
    privacyPolicyUrl,
    unsubscribeMailbox: emailConfig?.unsubscribeMailbox,
    optOutUrl: (leadId, channel) => {
      const token = buildOptOutToken(leadId, channel);
      return `${brandHomepageUrl}/api/outreach/opt-out?leadId=${leadId}&channel=${channel}&token=${token}`;
    },
    completeProfileUrl: (leadId) => {
      const token = buildCompleteProfileToken(leadId);
      return `${brandHomepageUrl}/complete-profile?leadId=${leadId}&token=${token}`;
    },
  };
}
